'use client';

import { useEffect, useState } from 'react';

import './styles.css';

import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import { BlockReference } from '@/cms/types/block';
import { BlockComponentModel } from '@/cms/types/blok-component';
import { ImageStrapi as IImageStrapi } from '@/cms/types/image';

import { getImageLinkTrackerContext } from '@/decodo/blocks/Footer/FooterStrapi';
import Button from '@/decodo/components/atoms/Button';
import ImageStrapi, {
  ImageStrapiProps,
} from '@/decodo/components/atoms/Image/ImageStrapi';
import { LinkButtonStrapiProps } from '@/decodo/components/atoms/LinkButton/LinkStrapi';
import { PopoverProps } from '@/decodo/components/atoms/Popover';
import { ToggleCMSProps } from '@/decodo/components/atoms/Toggle/useCMSToggle';
import SocialWidgetItem from '@/decodo/components/organisms/SocialWidget/SocialWidgetItem';

import LiveChat from './LiveChat';

import Close from '~/icons/x-close-02.svg';

export type SocialWidgetEntry = {
  icon: ImageStrapiProps;
  link: LinkButtonStrapiProps;
  tab?: ToggleCMSProps;
  popup?: ImageStrapiProps[];
};

type Position = 'right' | 'left';
export type SocialWidgetStrapiProps = {
  unfolded: boolean;
  position: Position;
  items: SocialWidgetEntry[];
  actionFloatingButton: BlockReference<IImageStrapi>;
};

const getOffsetForPosition = (position: Position): `${Position}-${number}` =>
  position === 'left' ? 'left-10' : 'right-10';

export const SocialWidgetStrapi = (
  props: BlockComponentModel<SocialWidgetStrapiProps>
) => {
  const { actionFloatingButton, items, position, unfolded } = props.block;

  const [isUnfolded, setIsUnfolded] = useState<boolean>(unfolded ?? false);
  const [liveChatReady, setLiveChatReady] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState<number | null>(null);
  const { texts } = useTranslation();

  useEffect(() => {
    if (!liveChatReady) return;
    setLiveChatVisibile(props.block.unfolded);
  }, [unfolded, liveChatReady, props.block.unfolded]);

  const setLiveChatVisibile = (val: boolean) => {
    const container = document.querySelector(
      '#chat-widget-container'
    ) as HTMLDivElement;

    if (val) {
      container.style.visibility = 'visible';
      container.style.opacity = '1';
    } else {
      container.style.visibility = 'hidden';
      container.style.opacity = '0';
    }
  };

  const handleLiveChatReady = () => {
    setLiveChatReady(true);
    const container = document.querySelector('#chat-widget-container');
    const minimizedContainer = document.querySelector('#chat-widget-minimized');

    if (container)
      container.classList.add(
        'position-hack-for-live-chat-widget',
        `position-hack-for-live-chat-widget-${props.block.position}`
      );

    if (minimizedContainer)
      minimizedContainer.classList.add(
        'position-hack-for-live-chat-widget',
        `position-hack-for-live-chat-widget-${props.block.position}`
      );
  };

  const toggleUnfolded = () => {
    setLiveChatVisibile(!isUnfolded);
    setIsUnfolded((prev) => !prev);
    setActiveTabIndex(null);
  };

  const popupProps: Partial<PopoverProps> = {
    position: props.block.position === 'right' ? 'left' : 'right',
  };

  function handleSelect(index: number) {
    index !== activeTabIndex
      ? setActiveTabIndex(index)
      : setActiveTabIndex(null);
  }

  const isLiveChatVisible = liveChatReady && isUnfolded;

  return (
    <>
      <div
        className={cn(
          'light',
          'bg-base-white fixed bottom-[180px] z-50 flex flex-col rounded-full p-3',
          getOffsetForPosition(position),
          'shadow-popup',
          isLiveChatVisible ? 'visible' : 'hidden'
        )}
        id='social-button-container'
      >
        {items?.map((item, index) => {
          return (
            <div key={index}>
              <div
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-full border border-gray-300',
                  activeTabIndex !== index && 'hover:border-brand-500',
                  activeTabIndex === index && 'border-brand-500 border-2',
                  index !== 0 && 'mt-2'
                )}
              >
                <SocialWidgetItem
                  tab={item.tab}
                  icon={item.icon}
                  key={index}
                  link={item.link}
                  handleSelectCallback={() => handleSelect(index)}
                  popup={item.popup}
                  PopupProps={popupProps}
                  data-tracker-context={getImageLinkTrackerContext(
                    'social-widget',
                    item.link?.url,
                    index
                  )}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div
        className={cn(
          'bg-brand-500 text-base-white text-body-m fixed bottom-[224px] right-[18px] z-20 flex flex-col items-center rounded-r-xl px-1 py-2 shadow-lg',
          isLiveChatVisible ? 'visible' : 'hidden'
        )}
      >
        {texts.social_widget.key_account_manager
          .split('')
          .map((char: string, idx: number) => (
            <span key={idx}>{char}</span>
          ))}
      </div>

      <div
        className={cn(
          'bg-brand-500 text-base-white text-body-m fixed bottom-[120px] right-[108px] z-20 flex flex-col items-center rounded-full px-2 py-1 shadow-lg',
          isLiveChatVisible ? 'visible' : 'hidden'
        )}
      >
        {texts.social_widget.customer_support}
      </div>
      <Button
        onClick={toggleUnfolded}
        className={cn(
          'bg-brand-500 fixed bottom-8 z-50 h-[60px] w-[60px] rounded-full border-none',
          'shadow-popup',
          getOffsetForPosition(position)
        )}
      >
        <Close
          className={cn(
            'light text-base-white h-10 w-10',
            !isUnfolded && 'hidden'
          )}
        />
        <div className={isUnfolded ? 'hidden' : ''}>
          <ImageStrapi
            block={{
              image: actionFloatingButton,
            }}
          />
        </div>
      </Button>

      <LiveChat onReady={handleLiveChatReady} />
    </>
  );
};
export default SocialWidgetStrapi;
